import { useEffect } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';
import StagePreparation from '../../shared/components/StagePreparation';
import {
  drumSelectedSongAtom,
  drumDifficultyAtom,
  drumPreparationStatusAtom,
  drumHasDrumPassAtom,
  drumLanguageAtom,
  drumPurchaseTargetAtom,
  drumPurchaseStateAtom,
  type Difficulty,
  type PurchaseState,
} from '../../shared/store/drumAppAtoms';
import { canPlayDifficulty } from '../../shared/utils/drumHelpers';
import './Preparation.scss';

function Preparation() {
  const song = useAtomValue(drumSelectedSongAtom);
  const [difficulty, setDifficulty] = useAtom(drumDifficultyAtom);
  const [preparationStatus, setPreparationStatus] = useAtom(drumPreparationStatusAtom);
  const hasDrumPass = useAtomValue(drumHasDrumPassAtom);
  const language = useAtomValue(drumLanguageAtom);
  const setPurchaseTarget = useSetAtom(drumPurchaseTargetAtom);
  const setPurchaseState = useSetAtom(drumPurchaseStateAtom);
  const navigate = useNavigate();

  useEffect(() => {
    if (!song) navigate('/drum/library', { replace: true });
  }, [song, navigate]);

  useEffect(() => {
    if (preparationStatus !== 'loading') return undefined;
    const timer = window.setTimeout(() => setPreparationStatus('ready'), 1100);
    return () => window.clearTimeout(timer);
  }, [preparationStatus, setPreparationStatus]);

  if (!song) return null;

  const openPurchase = (level: Difficulty) => {
    setDifficulty(level);
    setPurchaseTarget({ song, difficulty: level });
    const requestedState = new URLSearchParams(window.location.search).get('purchaseState') as PurchaseState | null;
    const allowedStates: PurchaseState[] = ['idle', 'checking', 'processing', 'success', 'cancelled', 'failed', 'restored'];
    setPurchaseState(requestedState && allowedStates.includes(requestedState) ? requestedState : 'idle');
  };

  const startPreparedDifficulty = (level: Difficulty) => {
    setDifficulty(level);
    if (!canPlayDifficulty(song, level, hasDrumPass)) {
      openPurchase(level);
      return;
    }
    navigate('/drum/game');
  };

  return (
    <main className="preparation-screen" data-localized-root style={{ backgroundImage: `linear-gradient(rgba(11, 18, 27, .48), rgba(11, 18, 27, .64)), url("${song.thumbnail}")` }}>
      <StagePreparation
        song={song}
        difficulty={difficulty}
        language={language}
        status={preparationStatus}
        hasDrumPass={hasDrumPass}
        onDifficulty={setDifficulty}
        onReady={startPreparedDifficulty}
        onRetry={() => setPreparationStatus('loading')}
        onBack={() => navigate('/drum/library')}
      />
    </main>
  );
}

export default Preparation;
